import { useState, useEffect } from 'react';
import { Logo } from './Logo';
import { api } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import type { Company } from '../types';

interface LogoSlideshowProps {
  size?: 'sm' | 'lg';
  alt?: string;
  interval?: number; // in milliseconds
}

/**
 * LogoSlideshow component
 * Displays a slideshow alternating between default logo and company logos
 * with smooth fade transitions every 5 seconds
 * 
 * Note: For administrator role, only shows default logo (no slideshow)
 */
export function LogoSlideshow({ 
  size = 'lg', 
  alt = 'Logo', 
  interval = 5000 
}: LogoSlideshowProps) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Check if user is administrator
  const isAdministrator = user?.role === 'administrator';

  // Fetch companies with logos (skip for administrator)
  useEffect(() => {
    // Don't fetch companies for administrator
    if (isAdministrator) {
      return;
    }

    const fetchCompanies = async () => {
      try {
        const data = await api.getAllCompanies();
        // Filter companies that have logos
        const companiesWithLogos = data.filter(c => c.logo_company);
        setCompanies(companiesWithLogos);
      } catch (error) {
        console.error('Error fetching companies for logo slideshow:', error);
      }
    };

    fetchCompanies();
  }, [isAdministrator]);

  // Create slides array: [default logo, ...company logos]
  const slides = [
    { type: 'default' as const, logo: null },
    ...companies.map(c => ({ type: 'company' as const, logo: c.logo_company, name: c.nama }))
  ];

  // Auto-advance slideshow
  useEffect(() => {
    // Don't run slideshow for administrator
    if (isAdministrator) {
      return;
    }

    // Only run slideshow if there are company logos
    if (slides.length <= 1) return;

    const timer = setInterval(() => {
      setIsTransitioning(true);
      
      // Wait for fade out
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % slides.length);
        setIsTransitioning(false);
      }, 300); // Half of transition duration
    }, interval);

    return () => clearInterval(timer);
  }, [slides.length, interval, isAdministrator]);

  // If administrator, just show default logo without slideshow
  if (isAdministrator) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Logo size={size} alt={alt} />
      </div>
    );
  }

  const currentSlide = slides[currentIndex];

  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
      <style>{`
        @keyframes fadeInOut {
          0%, 100% { opacity: 0; }
          10%, 90% { opacity: 1; }
        }
        
        .logo-slide {
          transition: opacity 600ms cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
      
      {/* Default Logo */}
      <div
        className={`absolute inset-0 flex items-center justify-center logo-slide ${
          currentSlide.type === 'default' && !isTransitioning
            ? 'opacity-100'
            : 'opacity-0'
        }`}
      >
        <Logo size={size} alt={alt} />
      </div>

      {/* Company Logos */}
      {companies.map((company) => (
        <div
          key={company.id}
          className={`absolute inset-0 flex items-center justify-center logo-slide ${
            currentSlide.type === 'company' && 
            currentSlide.logo === company.logo_company && 
            !isTransitioning
              ? 'opacity-100'
              : 'opacity-0'
          }`}
        >
          {company.logo_company && (
            <img
              src={company.logo_company}
              alt={company.nama}
              className={`object-contain ${
                size === 'sm' ? 'h-8' : 'p-8'
              }`}
            />
          )}
        </div>
      ))}

      {/* Slideshow indicator dots (optional, only show if multiple slides) */}
      {/* {slides.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1.5 pb-2">
          {slides.map((_, idx) => (
            <div
              key={idx}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                idx === currentIndex
                  ? 'bg-blue-600 w-4'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      )} */}
    </div>
  );
}
